/**
 * NotificationAvatar
 *
 * The circle a notification leads with, wherever it is shown — the header
 * dropdown, the inbox and a live toast. A notification from a person leads
 * with that person; one from the app itself leads with its kind's icon on a
 * `Medallion`. Both are the same circle at the same size, so a row's leading
 * shape is a property of the row, not of whether a sender exists.
 *
 * The icon shape carries the kind, so colour stays reserved for genuine
 * alerts: the medallion is neutral zinc, and red only for a failure — an
 * all-PR feed shouldn't read as a wall of green (or pink).
 */

import {
  getNotificationPresentation,
  getNotificationSender,
} from '../notificationPresentation';

import type { NotificationAvatarProps } from './NotificationAvatar.types';

import { Avatar, Medallion } from '@/components/ui';

export function NotificationAvatar({
  notification,
  size = 'md',
}: NotificationAvatarProps): React.JSX.Element {
  const sender = getNotificationSender(notification);

  if (sender) {
    return (
      <Avatar
        src={sender.avatar_url}
        name={sender.username}
        size={size}
        tone="raised"
        data-testid="notification-avatar"
      />
    );
  }

  const { Icon, tone } = getNotificationPresentation(notification);

  return (
    <Medallion
      icon={<Icon />}
      size={size}
      tone={tone}
      shape="circle"
      data-testid="notification-medallion"
    />
  );
}

export default NotificationAvatar;
